let workerInitialized = false;

/**
 * Generates a thumbnail blob URL from a PDF file by rendering the first page to canvas.
 * Uses dynamic imports to avoid loading PDF.js during SSR.
 *
 * @param file - PDF file to generate thumbnail from
 * @returns Promise resolving to blob URL
 * @throws Error if thumbnail generation fails or if called in SSR context
 */
export async function generatePdfThumbnail(file: File): Promise<string> {
	// Guard: Ensure we're in a browser context (not SSR)
	if (typeof window === 'undefined' || typeof document === 'undefined') {
		throw new Error('PDF thumbnail generation requires browser environment');
	}

	// Dynamic import to prevent loading during SSR bundle creation
	const { getDocument, GlobalWorkerOptions } = await import('pdfjs-dist/legacy/build/pdf');

	// Initialize PDF.js worker once
	if (!workerInitialized) {
		GlobalWorkerOptions.workerSrc = require('pdfjs-dist/legacy/build/pdf.worker.entry');
		workerInitialized = true;
	}

	// Load PDF and get first page
	const arrayBuffer = await file.arrayBuffer();
	const pdf = await getDocument({ data: arrayBuffer }).promise;
	const page = await pdf.getPage(1);

	// Calculate scale to fit thumbnail (max 200px for quality + reasonable memory)
	const viewport = page.getViewport({ scale: 1 });
	const scale = Math.min(200 / viewport.width, 200 / viewport.height);
	const scaledViewport = page.getViewport({ scale });

	// Create and render canvas
	const canvas = document.createElement('canvas');
	const context = canvas.getContext('2d');
	if (!context) {
		throw new Error('Canvas 2D context unavailable');
	}

	canvas.width = scaledViewport.width;
	canvas.height = scaledViewport.height;

	await page.render({ canvasContext: context, viewport: scaledViewport }).promise;

	return new Promise((resolve, reject) => {
		canvas.toBlob((blob) => {
			if (blob) {
				resolve(URL.createObjectURL(blob));
			} else {
				reject(new Error('Failed to generate blob from canvas'));
			}
		}, 'image/png');
	});
}
